import PortfolioConfigData from "config/portfolio-config.json";
import React, { memo, useCallback, useMemo } from "react";

import PortfolioTable from "./ComposizioneDelPortafoglioTable/ComposizioneDelPortafoglioTable";

import colorsStore from "shared/store/colorsStore";
import styles from "./ComposizioneDelPortafoglio.module.scss";
import { ProductData } from "./types";
import { hideGlobalTooltip, showGlobalTooltip } from "./utils";
import GlobalTooltip from "./GlobalTooltip";
import useStrumentiSort from "./hooks/useStrumentiSort";

// const COLUMN_WIDTHS = [15, 8, 30, 8, 9, 20, 9, 8, 7];
// const COLUMN_WIDTHS = [120, 70, 220, 80, 90, 180, 90, 80, 60]; // pixel values
const COLUMN_WIDTHS = [100, 64, 200, 54, 64, 160, 70, 54, 78, 55, 65]; // pixel values

interface ComposizioneDelPortafoglioProps {
  strumenti_fondi: ProductData[];
  strumenti_obbligazionari: ProductData[];
}

const ComposizioneDelPortafoglio = memo(
  ({
    strumenti_fondi,
    strumenti_obbligazionari,
  }: ComposizioneDelPortafoglioProps) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);

    // Ensure we only take the first 9 headers
    const headersFondi = useMemo(() => {
      const shouldRemoveControvalore = strumenti_fondi.every(
        (product) => product["controvalore"] === 0,
      );

      return strumenti_fondi && strumenti_fondi.length > 0
        ? Object.keys(strumenti_fondi[0])
            .filter(
              (key) =>
                key &&
                (!shouldRemoveControvalore || key !== "controvalore") &&
                key !== "investimento_minimo" &&
                key !== "final_score" &&
                key !== "rating_sostenibilita_morningstar",
            )
            .slice(0, 9)
        : [];
    }, [strumenti_fondi]);

    // Ensure we only take the first 9 headers
    const headersObbligazionari = useMemo(() => {
      const shouldRemoveControvalore = strumenti_obbligazionari.every(
        (product) => product["controvalore"] === 0,
      );

      return strumenti_obbligazionari && strumenti_obbligazionari.length > 0
        ? Object.keys(strumenti_obbligazionari[0])
            .filter(
              (key) =>
                key &&
                (!shouldRemoveControvalore || key !== "controvalore") &&
                key !== "investimento_minimo" &&
                key !== "final_score" &&
                key !== "rating_sostenibilita_morningstar",
            )
            .slice(0, 11)
        : [];
    }, [strumenti_obbligazionari]);

    const {
      sortConfig: sortConfigFondi,
      sortedData: sortedDataFondi,
      handleSort: handleSortFondi,
    } = useStrumentiSort(strumenti_fondi);
    const {
      sortConfig: sortConfigObbligazionari,
      sortedData: sortedDataObbligazionari,
      handleSort: handleSortObbligazionari,
    } = useStrumentiSort(strumenti_obbligazionari);

    const columnMappingsFondi =
      PortfolioConfigData.portfolio.data.strumenti_fondi.columns;
    const columnMappingsObbligazionari =
      PortfolioConfigData.portfolio.data.strumenti_obbligazionari.columns;

    // These functions don't update component state directly
    const handleTooltipTrigger = useCallback(
      (event: React.MouseEvent, text: string) => {
        showGlobalTooltip(event, text);
      },
      [],
    );

    const handleTooltipHide = useCallback(() => {
      hideGlobalTooltip();
    }, []);

    return (
      <div className={styles.complexTableContainer}>
        <h4 className={styles.tableTitle}>Fondi, SICAV, ETF, ETC, ETN</h4>
        <div className={styles.tableContainerInner}>
          <PortfolioTable
            data={sortedDataFondi}
            headers={headersFondi}
            sortConfig={sortConfigFondi}
            columnMappings={columnMappingsFondi}
            columnWidths={COLUMN_WIDTHS}
            colorsConfig={colorsConfig}
            handleSort={handleSortFondi}
            onTooltipTrigger={handleTooltipTrigger}
            onTooltipHide={handleTooltipHide}
          />
          <GlobalTooltip />
        </div>
        {sortedDataObbligazionari.length > 0 && (
          <>
            <h4 className={styles.tableTitle}>Titoli Obbligazionari</h4>
            <div className={styles.tableContainerInner}>
              <PortfolioTable
                data={sortedDataObbligazionari}
                headers={headersObbligazionari}
                sortConfig={sortConfigObbligazionari}
                columnMappings={columnMappingsObbligazionari}
                columnWidths={COLUMN_WIDTHS}
                colorsConfig={colorsConfig}
                handleSort={handleSortObbligazionari}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
              />
              <GlobalTooltip />
            </div>
          </>
        )}
      </div>
    );
  },
);

export default ComposizioneDelPortafoglio;
